import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { http, formatDetail } from "./api";
import { useNoIndex } from "../lib/seo";

export default function PortalLogin() {
  useNoIndex();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [forgot, setForgot] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mfaToken) {
        const { data } = await http.post("/auth/verify-mfa", { mfa_token: mfaToken, code: code.trim() });
        if (data.role === "client") navigate("/portal");
      } else if (forgot) {
        await http.post("/portal/forgot-password", { email });
        toast.success("If that account exists, a reset link has been emailed.");
        setForgot(false);
      } else {
        const { data } = await http.post("/auth/login", { email, password });
        if (data.mfa_required) {
          setMfaToken(data.mfa_token);
          if (data.email_sent === false) {
            toast.error("We could not email your code. Use 'Resend code' or contact Bigibila.");
          } else {
            toast.success("A 6-digit sign-in code has been emailed to you.");
          }
        } else if (data.role !== "client") {
          toast.error("This sign-in is for clients. Administrators use /admin.");
        } else {
          navigate("/portal");
        }
      }
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      const { data } = await http.post("/auth/resend-mfa", { mfa_token: mfaToken });
      setMfaToken(data.mfa_token);
      toast.success("A new code has been emailed.");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-ink">
      <form onSubmit={submit} data-testid="portal-login-form" className="w-full max-w-sm border border-line bg-surface1 p-8 md:p-10">
        <div className="flex items-center gap-3 mb-4">
          <img src="/bigibila-logo.png" alt="Bigibila" className="h-10 w-10" />
          <div>
            <p className="font-serif text-xl text-bone">Bigibila</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ember">Client portal</p>
          </div>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-stone2 mb-8 border-l-2 border-ember pl-3">Confidential — authorised clients only. Access is logged.</p>
        {mfaToken ? (
          <div data-testid="mfa-step">
            <label className="font-mono text-xs uppercase tracking-[0.2em] text-stone2 block mb-2" htmlFor="portal-mfa-code">Enter the 6-digit code emailed to you</label>
            <input id="portal-mfa-code" inputMode="numeric" maxLength={6} data-testid="portal-mfa-input" className="field-dark w-full px-4 py-3 text-lg tracking-[0.5em] text-center mb-6" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" autoFocus />
          </div>
        ) : (
          <>
            <label className="font-mono text-xs uppercase tracking-[0.2em] text-stone2 block mb-2" htmlFor="portal-email">Email</label>
            <input id="portal-email" type="email" data-testid="portal-email-input" className="field-dark w-full px-4 py-3 text-sm mb-5" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@organisation.org.au" />
            {!forgot && (
              <>
                <label className="font-mono text-xs uppercase tracking-[0.2em] text-stone2 block mb-2" htmlFor="portal-password">Password</label>
                <input id="portal-password" type="password" data-testid="portal-password-input" className="field-dark w-full px-4 py-3 text-sm mb-6" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
              </>
            )}
          </>
        )}
        <button type="submit" disabled={busy || (mfaToken && code.length !== 6)} data-testid="portal-login-btn" className="w-full font-mono text-sm uppercase tracking-[0.2em] px-6 py-3.5 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300 disabled:opacity-60">
          {busy ? "Please wait…" : mfaToken ? "Verify code" : forgot ? "Send reset link" : "Sign in"}
        </button>
        <div className="flex justify-between items-center mt-6">
          {mfaToken ? (
            <>
              <button type="button" data-testid="portal-mfa-resend" onClick={resend} className="font-mono text-[11px] uppercase tracking-[0.15em] text-ochre hover:text-ember transition-colors duration-300">Resend code</button>
              <button type="button" data-testid="portal-mfa-back" onClick={() => { setMfaToken(""); setCode(""); }} className="font-mono text-[11px] uppercase tracking-[0.15em] text-stone2 hover:text-bone transition-colors duration-300">Back</button>
            </>
          ) : (
            <>
              <button type="button" data-testid="portal-forgot-toggle" onClick={() => setForgot(!forgot)} className="font-mono text-[11px] uppercase tracking-[0.15em] text-ochre hover:text-ember transition-colors duration-300">
                {forgot ? "Back to sign in" : "Forgot password?"}
              </button>
              <Link to="/" className="font-mono text-[11px] uppercase tracking-[0.15em] text-stone2 hover:text-bone transition-colors duration-300">bigibila.com.au</Link>
            </>
          )}
        </div>
      </form>
    </div>
  );
}
